import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Calendar, Trash2, Check } from "lucide-react";
import { MdExpandMore } from "react-icons/md";
import { BASE_URL } from "utils/constant";

/* --------- tiny helpers --------- */
const toDisplay = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
};
const toISO = (display) => {
  if (!display || !display.includes("/")) return "";
  const [d, m, y] = display.split("/");
  if (!d || !m || !y) return "";
  return `${y.padStart(4, "0")}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

/* ===================== Material Selection Modal ===================== */
function MaterialSelectionModal({
  isOpen,
  onClose,
  onSelectMaterial,
  selectedMaterials = [],
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const organisationId = JSON.parse(localStorage.getItem("organization"))?.id;
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const getMaterials = async () => {
    const res = await fetch(`${BASE_URL}/org/material-library/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ org_id: organisationId }),
    });
    const data = await res.json();
    setAvailableMaterials(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      getMaterials();
    }
  }, [isOpen]);

  const filteredMaterials = availableMaterials.filter((material) => {
    const name = (material?.material_name || "").toLowerCase();
    const sub = (material?.subcategory || "").toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || sub.includes(q);
  });

  const handleMaterialToggle = (material, alreadyInRequest) => {
    if (alreadyInRequest) return;
    setSelectedIds((prev) =>
      prev.includes(material.id)
        ? prev.filter((id) => id !== material.id)
        : [...prev, material.id],
    );
  };

  const handleAddSelected = () => {
    if (!selectedIds.length) return;
    const toAdd = availableMaterials.filter((m) => selectedIds.includes(m.id));
    toAdd.forEach((m) => onSelectMaterial(m));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-[37vw] bg-white rounded-2xl px-3 py-2 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex flex-row items-center py-2 justify-between border-b-[0.5px] border-[#A3A3A3]">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
          >
            <X size={20} />
          </button>
          <div className="text-lg font-normal flex flex-row items-center text-[#232323]">
            Select Material
          </div>
          <div className="flex w-[10%] flex-col items-center"></div>
        </div>

        <div className="flex-1 overflow-hidden py-2 flex flex-col">
          <div className="pb-2 mt-2 flex flex-col w-full items-left">
            <span className="text-sm font-normal pl-1 w-full text-[#232323]">
              Select from your material inventory
            </span>
            <div className="relative w-full flex flex-col items-left">
              <input
                type="text"
                placeholder="Search Your Material"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-1 w-[97%] ml-[2px] mt-1 text-base bg-[#F3F4F6] rounded-lg border-0 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-4">
            <div className="space-y-1">
              {filteredMaterials.map((material) => {
                const alreadyInRequest = selectedMaterials.some(
                  (s) => s.id === material.id,
                );
                const isNewlySelected = selectedIds.includes(material.id);
                const isSelected = alreadyInRequest || isNewlySelected;
                return (
                  <button
                    key={material.id}
                    onClick={() =>
                      handleMaterialToggle(material, alreadyInRequest)
                    }
                    className={`w-full flex items-center gap-3 px-2 py-1 border-b-[0.5px] border-[#C3C3C3] text-left transition
                      ${
                        alreadyInRequest
                          ? "cursor-not-allowed opacity-70"
                          : isNewlySelected
                            ? "bg-purple-50"
                            : "hover:bg-gray-50"
                      }`}
                    disabled={alreadyInRequest}
                    aria-disabled={alreadyInRequest}
                    title={
                      alreadyInRequest
                        ? "Already added"
                        : isNewlySelected
                          ? "Click to remove"
                          : "Click to select"
                    }
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${
                          alreadyInRequest
                            ? "bg-gray-300 border-gray-300"
                            : isNewlySelected
                              ? "bg-[#3B0069] border-[#3B0069]"
                              : "border-[#232323]"
                        }`}
                    >
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[#232323]">
                        {material.material_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      {material?.specification && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.specification}
                        </span>
                      )}
                      {material?.subcategory && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.subcategory}
                        </span>
                      )}
                      {material?.uom && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.uom}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredMaterials.length === 0 && (
                <div className="text-sm text-gray-500 p-4">
                  No materials found.
                </div>
              )}
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {selectedIds.length
                ? `${selectedIds.length} material${selectedIds.length > 1 ? "s" : ""} selected`
                : "Select one or more materials to add"}
            </span>
            <button
              onClick={handleAddSelected}
              disabled={selectedIds.length === 0}
              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[#3B0069] disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#3B0069D9] transition-colors"
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== Main Component ===================== */
export default function CreateMaterialRequest({ onClose, onCreateMR }) {
  const [deliveryDate, setDeliveryDate] = useState(
    new Date().toISOString().slice(0, 10).split("-").reverse().join("/"),
  );
  const [materials, setMaterials] = useState([]);
  const [showMaterialSelector, setShowMaterialSelector] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const hiddenDateInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Dynamic MR number
  const [mrNumber, setMrNumber] = useState(null);
  const [mrNumberLoading, setMrNumberLoading] = useState(true);

  // Fetch next MR number on mount
  useEffect(() => {
    const fetchMRNumber = async () => {
      setMrNumberLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/inventory/material-requests/next-number/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );
        const data = await res.json();
        // Adjust key to match your actual API response
        const number =
          data?.mr_number || data?.next_number || data?.number || null;
        setMrNumber(number);
      } catch {
        setMrNumber(null);
      } finally {
        setMrNumberLoading(false);
      }
    };
    fetchMRNumber();
  }, []);

  const handleCalendarClick = () => {
    const iso = toISO(deliveryDate);
    if (hiddenDateInputRef.current && iso) {
      hiddenDateInputRef.current.value = iso;
    }
    if (hiddenDateInputRef.current?.showPicker) {
      hiddenDateInputRef.current.showPicker();
    } else {
      hiddenDateInputRef.current?.focus();
      hiddenDateInputRef.current?.click();
    }
  };

  const handleHiddenDateChange = (e) => {
    const iso = e.target.value;
    const display = toDisplay(iso);
    if (display) setDeliveryDate(display);
  };

  const handleAddMaterial = () => setShowMaterialSelector(true);

  const handleSelectMaterial = (material) => {
    setMaterials((prev) => {
      if (prev.some((m) => m.id === material.id)) return prev;
      return [...prev, { ...material, quantity: "", uom: material?.uom }];
    });
  };

  const handleRemoveMaterial = (index) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, quantity) => {
    setMaterials((prev) =>
      prev.map((material, i) =>
        i === index
          ? { ...material, quantity: Math.max(0, parseInt(quantity)) }
          : material,
      ),
    );
  };

  const handleSubmit = async () => {
    setErrorMsg("");
    const dueISO = toISO(deliveryDate);
    if (!selectedSite) {
      setErrorMsg("Please select a site.");
      return;
    }
    if (!dueISO) {
      setErrorMsg("Please choose a valid delivery date.");
      return;
    }
    if (!materials.length) {
      setErrorMsg("Please add at least one material.");
      return;
    }
    for (const m of materials) {
      if (!m.quantity || isNaN(m.quantity) || Number(m.quantity) <= 0) {
        setErrorMsg(`Please enter a valid quantity for ${m.material_name}.`);
        return;
      }
    }

    const payload = {
      sites: Number(selectedSite),
      due_date: dueISO,
      status: "OPEN",
      lines: materials.map((m) => ({
        material: m.id,
        quantity: Number(m.quantity) || 1,
      })),
    };

    try {
      setSubmitting(true);
      const res = await fetch(
        `${BASE_URL}/inventory/material-requests/create/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data?.detail ||
          data?.message ||
          (typeof data === "string" ? data : "Failed to create request.");
        throw new Error(msg);
      }
      onCreateMR?.(data);
      onClose();
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const getSites = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    const res = await fetch(`${BASE_URL}/sites/organizations/${orgId}/sites/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
    const data = await res.json();
    setSites(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    getSites();
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />

        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl mx-4 max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-center py-6 border-b border-gray-100 relative">
            <h2 className="text-xl font-medium text-[#232323]">
              New Material Request
            </h2>
            <button
              onClick={onClose}
              className="absolute right-6 top-6 w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* MR Number — now dynamic */}
            {/* <div className="mb-6 flex flex-row items-center gap-4">
              <label className="block text-sm font-medium text-[#232323]">
                Material request No.
              </label>
              <div className="text-[#232323] font-normal text-sm text-center px-3 py-1 rounded-2xl bg-[#F3F4F6] min-w-[80px]">
                {mrNumberLoading
                  ? "Loading..."
                  : mrNumber
                    ? `#${mrNumber}`
                    : "—"}
              </div>
            </div> */}

            <div className="mb-8">
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Select Delivery Date
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Select date"
                  readOnly
                />
                <button
                  type="button"
                  onClick={handleCalendarClick}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                  aria-label="Open calendar"
                >
                  <Calendar size={20} />
                </button>
                <input
                  ref={hiddenDateInputRef}
                  type="date"
                  onChange={handleHiddenDateChange}
                  className="sr-only absolute right-56 top-12 pointer-events-none"
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Select Site
              </label>
              <div className="relative">
                <select
                  value={selectedSite || ""}
                  onChange={(e) =>
                    setSelectedSite(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
                >
                  <option value="">Select a site</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.site_name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                  <MdExpandMore size={24} />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#232323]">
                    Materials
                  </span>
                  <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full min-w-[24px] text-center">
                    {materials.length}
                  </span>
                </div>
                <button
                  onClick={handleAddMaterial}
                  className="w-8 h-8 bg-[#ECE6F0] rounded-full flex items-center justify-center text-[#3B0069] hover:bg-purple-100 transition-colors"
                  title="Add material"
                >
                  <Plus size={16} />
                </button>
              </div>

              <div className="space-y-3">
                {materials.map((material, index) => (
                  <div
                    key={`${material.id}-${index}`}
                    className="flex items-center justify-between w-full gap-3"
                  >
                    {/* <div
                      className="flex flex-row cursor-pointer items-center w-[55%] justify-between px-3 py-2 rounded-lg bg-[#F3F4F6]"
                      onClick={() => setShowMaterialSelector(true)}
                    >
                      <div className="w-full bg-transparent border-0 text-[#232323] font-normal focus:outline-none">
                        {material.material_name}
                      </div>
                      <span className="flex flex-col items-center justify-center">
                        <MdExpandMore color="#232323" size={24} />
                      </span>
                    </div> */}
                    <div className="flex flex-row items-center w-[55%] px-3 py-2 rounded-lg bg-[#F3F4F6]">
                      <div className="w-full bg-transparent border-0 text-[#232323] font-normal focus:outline-none">
                        {material.material_name}
                      </div>
                    </div>
                    <div className="flex flex-row items-center w-[35%] justify-between px-2 py-2 rounded-lg bg-[#F3F4F6]">
                      <input
                        type="number"
                        value={material.quantity}
                        placeholder="Qty"
                        onChange={(e) =>
                          handleQuantityChange(index, e.target.value)
                        }
                        style={{
                          WebkitAppearance: "textfield",
                          margin: 0,
                          MozAppearance: "textfield",
                        }}
                        className="w-[45%] text-center bg-transparent text-[#232323] font-normal focus:outline-none"
                        min="1"
                      />
                      <div className="border-l h-6 border-[#C3C3C3] mx-1" />
                      <div className="bg-transparent w-[40%] border-0 text-center text-[#232323] focus:outline-none">
                        {material?.uom ?? "Unit"}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveMaterial(index)}
                      className="w-8 h-8 text-red-500 hover:bg-red-50 bg-[#F9E7E7] rounded-full flex items-center justify-center transition-colors"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {errorMsg && (
                <div className="mt-4 text-sm text-red-600">{errorMsg}</div>
              )}
            </div>
          </div>

          <div className="p-6 border-t flex flex-col items-center justify-center border-gray-100">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ alignSelf: "center", opacity: submitting ? 0.8 : 1 }}
              className="w-[40%] bg-[#3B0069] hover:bg-[#3B0069D9] disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? "Submitting..." : "Material Request"}
              <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
                <Check size={18} />
              </span>
            </button>
          </div>
        </div>
      </div>

      <MaterialSelectionModal
        isOpen={showMaterialSelector}
        onClose={() => setShowMaterialSelector(false)}
        onSelectMaterial={handleSelectMaterial}
        selectedMaterials={materials}
      />
    </>
  );
}
